import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { Form, message, Select } from 'antd';
import { useTheme } from 'styled-components';
import { useStores } from '@/hooks/use-stores';
import ResponsiveModal from '@/comp/Forms/common/ResponsiveModal';
import InfoCard from '@/comp/Forms/common/InfoCard';
import { StageTaskType } from '@/store/Lifecycle/constants';
import { useStage } from '../Stage/Provider';
import { getTaskSectionMetadata } from './constants';
import { useTask } from './Provider';
import { computeValidateStatus } from './utils';

const UserIdTask = observer(() => {
  const { t } = useTranslation();
  const { token } = useTheme();
  const { lifecycleStore } = useStores();
  const { ownerUsers, getOwnerUsers } = lifecycleStore;
  const { id } = useStage();
  const { name, value, userFullName, userEmail, help, validateStatus, isReadOnly, required } =
    useTask<StageTaskType.UserId>();
  const [form] = Form.useForm();
  const [isEditing, setIsEditing] = useState(false);
  const [canSave, setCanSave] = useState(false);
  const { icon, title } = getTaskSectionMetadata(name);

  const computedValidateStatus = computeValidateStatus(value, validateStatus);

  const handleSave = async () => {
    const isSaved = await lifecycleStore.updateTask(id, name, form.getFieldValue(name));
    if (isSaved) {
      setIsEditing(false);
      setCanSave(false);
    } else {
      message.error(`${t('Failed to save')} ${t(name)}`);
    }
  };

  const onValuesChange = (_, allFields: Record<string, string>) => {
    setCanSave(!!allFields[name]);
  };

  useEffect(() => {
    if (isEditing) {
      form.resetFields();
      if (ownerUsers.length === 0) {
        getOwnerUsers();
      }
    }
  }, [isEditing]);

  return (
    <>
      <InfoCard
        icon={icon}
        title={t(title)}
        disabled={isReadOnly}
        validateStatus={computedValidateStatus}
        help={help}
        onEdit={() => setIsEditing(true)}
      >
        {userFullName && <span>{userFullName}</span>}
        {userEmail && <span>{userEmail}</span>}
      </InfoCard>
      <ResponsiveModal
        visible={isEditing}
        onClose={() => setIsEditing(false)}
        onSave={handleSave}
        title={t('Edit')}
        saveButtonText={t('Save Changes')}
        disableSaveButton={!canSave}
      >
        <Form form={form} name={`${name}-form`} onValuesChange={onValuesChange}>
          <Form.Item name={name} initialValue={value} required={required}>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder={t('Select User')}
              loading={!ownerUsers.length}
              disabled={!ownerUsers.length}
              options={ownerUsers.map(user => ({
                label: user.name,
                value: user.id,
                email: user.email,
              }))}
              optionRender={option => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: token.paddingXXS }}>
                  <span style={{ fontSize: token.fontSize, color: token.colorText }}>{option.data.label}</span>
                  <span style={{ fontSize: token.fontSizeSM, color: token.colorTextDescription }}>
                    {option.data.email}
                  </span>
                </div>
              )}
            />
          </Form.Item>
        </Form>
      </ResponsiveModal>
    </>
  );
});

export default UserIdTask;
